import React, { useRef, useEffect } from "react";
import * as d3 from "d3";
import { rccColors } from "../colors";
import { useRccTypography } from "../typography";

const FONT_FAMILY = '"Onest", sans-serif';

/**
 * D3SingleLineChart - Configuration-driven single line chart with dots and value labels
 * Matches the "Average Deal Size Trend" screenshot UI.
 *
 * @param {Object} props
 * @param {Array} props.data - Chart data array with shape:
 *   [{ quarter: string, value: number }] or [{ xAxisLabel: string, value: number }]
 * @param {Object} props.options - Optional configuration
 * @param {string} props.options.lineColor - Line color (default: blue[500])
 * @param {string} props.options.dotColor - Dot color (default: same as lineColor)
 * @param {string} props.options.lineStyle - Line style: 'solid' or 'dotted' (default: 'dotted')
 * @param {Function} props.options.yAxisFormatter - Function to format y-axis values (default: formats as K)
 * @param {Function} props.options.valueFormatter - Function to format values above points (default: formats as number)
 * @param {Function} props.options.xAxisFormatter - Function to format x-axis labels (default: identity)
 * @param {boolean} props.options.showValueLabels - Show values above points (default: true)
 * @param {boolean} props.options.showLegend - Show legend (default: true)
 * @param {string} props.options.legendLabel - Legend label text (default: '')
 * @param {number} props.options.marginTop - Top margin (default: 20)
 * @param {number} props.options.marginRight - Right margin (default: 30)
 * @param {number} props.options.marginBottom - Bottom margin (default: 40)
 * @param {number} props.options.marginLeft - Left margin (default: 45)
 * @param {number} props.options.dotRadius - Dot radius (default: 6)
 * @param {number} props.options.lineWidth - Line stroke width (default: 2)
 */
function D3SingleLineChart({ data, options = {} }) {
  const wrapperRef = useRef(null);
  const containerRef = useRef(null);
  const lastDimensions = useRef({ width: 0, height: 0 });
  const typography = useRccTypography();

  const {
    lineColor = rccColors.stackedBarPairedPalette[0].color,
    dotColor,
    lineStyle = "dotted",
    yAxisFormatter = (v) => {
      if (v >= 1000) {
        return `${(v / 1000).toFixed(1)}K`;
      }
      return v.toString();
    },
    valueFormatter = (v) => v.toFixed(1),
    xAxisFormatter = (v) => v,
    showValueLabels = true,
    showLegend = true,
    legendLabel = "",
    marginTop = 20,
    marginRight = 30,
    marginBottom = 40,
    marginLeft = 50, // Increased left margin for y-axis labels
    dotRadius = 6,
    lineWidth = 2,
  } = options;

  useEffect(() => {
    if (!wrapperRef.current || !containerRef.current || !data?.length) return;

    const renderChart = () => {
      const wrapper = wrapperRef.current;
      const container = containerRef.current;
      if (!wrapper || !container) return;

      const containerWidth = wrapper.clientWidth;
      const containerHeight = wrapper.clientHeight;

      const width = containerWidth - marginLeft - marginRight;
      const height = containerHeight - marginTop - marginBottom;

      if (width <= 0 || height <= 0) return;

      // Skip if dimensions haven't changed (prevents resize loop)
      const { width: lastW, height: lastH } = lastDimensions.current;
      if (containerWidth === lastW && containerHeight === lastH) return;

      // Store current dimensions before drawing
      lastDimensions.current = {
        width: containerWidth,
        height: containerHeight,
      };

      // Clear SVG
      d3.select(container).selectAll("*").remove();

      const svg = d3
        .select(container)
        .append("svg")
        .attr("width", containerWidth)
        .attr("height", containerHeight)
        .append("g")
        .attr("transform", `translate(${marginLeft},${marginTop})`);

      // Prepare data - filter out null/undefined values
      const validData = data
        .map((d) => ({
          quarter: d.quarter || d.xAxisLabel,
          value: d.value,
        }))
        .filter((pt) => pt.value !== undefined && pt.value !== null);

      if (validData.length === 0) return;

      // Scales
      const xDomain = validData.map((d) => d.quarter);
      const xScale = d3
        .scalePoint()
        .domain(xDomain)
        .range([0, width])
        .padding(0.5);

      const maxY = d3.max(validData, (d) => d.value) * 1.1; // Add 10% headroom
      const yScale = d3.scaleLinear().domain([0, maxY]).range([height, 0]);

      // Y-Axis Labels with tick marks (no gridlines)
      const yLabelAxis = d3
        .axisLeft(yScale)
        .ticks(5)
        .tickFormat(yAxisFormatter)
        .tickSize(6); // Set positive tick size to show tick marks on axis
      const yLabelGroup = svg.append("g").call(yLabelAxis);

      yLabelGroup
        .select(".domain")
        .attr("stroke", rccColors.neutral[100])
        .attr("stroke-width", 1);
      // Style tick marks (keep them, don't remove)
      yLabelGroup.selectAll("line").attr("stroke", rccColors.neutral[300]);
      yLabelGroup
        .selectAll("text")
        .attr("x", -10)
        .attr("class", typography.textExtraSmallRegular)
        .style("fill", rccColors.neutral[800])
        .style("font-family", FONT_FAMILY);

      // X-Axis
      const xAxis = d3.axisBottom(xScale).tickFormat(xAxisFormatter);
      const xAxisGroup = svg
        .append("g")
        .attr("transform", `translate(0,${height})`)
        .call(xAxis);

      xAxisGroup.select(".domain").attr("stroke", rccColors.neutral[200]);
      xAxisGroup.selectAll("line").remove();
      xAxisGroup
        .selectAll("text")
        .attr("y", 15)
        .attr("class", typography.textExtraSmallRegular)
        .style("fill", rccColors.neutral[800])
        .style("font-family", FONT_FAMILY);

      // Draw Line
      const lineGenerator = d3
        .line()
        .x((d) => xScale(d.quarter))
        .y((d) => yScale(d.value));

      const strokeDasharray = lineStyle === "dotted" ? "2,2" : "0";

      svg
        .append("path")
        .datum(validData)
        .attr("fill", "none")
        .attr("stroke", lineColor)
        .attr("stroke-width", lineWidth)
        .attr("stroke-dasharray", strokeDasharray)
        .attr("d", lineGenerator);

      // Draw Dots
      const finalDotColor = dotColor || lineColor;
      svg
        .selectAll(".dot")
        .data(validData)
        .enter()
        .append("circle")
        .attr("cx", (d) => xScale(d.quarter))
        .attr("cy", (d) => yScale(d.value))
        .attr("r", dotRadius)
        .attr("fill", finalDotColor)
        .attr("stroke", "#fff")
        .attr("stroke-width", 0);

      // Draw Value Labels above points
      if (showValueLabels) {
        svg
          .selectAll(".value-label")
          .data(validData)
          .enter()
          .append("text")
          .attr("x", (d) => xScale(d.quarter))
          .attr("y", (d) => yScale(d.value) - 12)
          .attr("text-anchor", "middle")
          .attr("class", typography.textExtraSmallSemibold)
          .style("fill", rccColors.neutral[800])
          .text((d) => valueFormatter(d.value));
      }
    };

    renderChart();

    const resizeObserver = new ResizeObserver(() => {
      setTimeout(renderChart, 50);
    });
    resizeObserver.observe(wrapperRef.current);

    return () => resizeObserver.disconnect();
  }, [
    data,
    options,
    lineColor,
    dotColor,
    lineStyle,
    yAxisFormatter,
    valueFormatter,
    xAxisFormatter,
    showValueLabels,
    marginLeft,
    marginTop,
    marginBottom,
    marginRight,
    dotRadius,
    lineWidth,
    typography,
  ]);

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        ref={wrapperRef}
        style={{
          width: "100%",
          flex: 1,
          position: "relative",
          minHeight: 0,
        }}
      >
        <div
          ref={containerRef}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflow: "hidden",
            margin: 0,
            padding: 0,
          }}
        />
      </div>
      {showLegend && legendLabel && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            paddingTop: "16px",
            gap: "8px",
          }}
        >
          <div
            style={{
              width: "6px",
              height: "6px",
              borderRadius: "50%",
              backgroundColor: dotColor || lineColor,
              flexShrink: 0,
            }}
          />
          <span
            className={typography.textSmallRegular}
            style={{ color: rccColors.neutral[800] }}
          >
            {legendLabel}
          </span>
        </div>
      )}
    </div>
  );
}

export default D3SingleLineChart;
